import type { TeamMember, ChatMessage, ChatSession, SessionType } from '../types/index.js';
import type {
  ClientFile,
  ClientFileInput,
  CoachInvite,
  CoachSession,
  CoachSessionWithDebrief,
  CoachSessionDebrief,
  CoachSessionSummary,
  SessionInput,
  SessionUpdate,
  DebriefUpdate,
  DebriefFieldName,
  PlanGenerationResult,
  TimelineResponse,
  Milestone,
  MilestoneInput,
  MilestonePatch,
  ReportSummary,
  ReportDetail,
  ReportDraft,
  ReportSaveInput,
  ReportPatch,
  ClientToolRow,
} from '../types/coach.js';
import type { ToolKey } from '@/lib/tools';

// Empty string → same-origin (Vite proxy in dev, deployed origin in prod)
// Explicit URL → direct API calls (e.g. staging env)
const BASE = import.meta.env['VITE_API_URL'] || '';

export class VoucherRequiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'VoucherRequiredError';
  }
}

async function request<T>(
  path: string,
  init?: RequestInit & { timeoutMs?: number },
): Promise<T> {
  const { timeoutMs, ...fetchInit } = init ?? {};
  const hasBody = fetchInit.body != null;
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs ?? 8000);
  let res: Response;
  try {
    res = await fetch(`${BASE}${path}`, {
      ...fetchInit,
      credentials: 'include',
      signal: controller.signal,
      headers: {
        ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
        ...fetchInit.headers,
      },
    });
  } finally {
    clearTimeout(timeout);
  }
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    if (res.status === 422) {
      try {
        const body = JSON.parse(text);
        if (body?.error === 'voucher_required') {
          throw new VoucherRequiredError(body.message ?? 'Voucher required');
        }
      } catch (err) {
        if (err instanceof VoucherRequiredError) throw err;
        // fall through to generic error
      }
    }
    throw new Error(`API ${res.status}: ${text}`);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

async function transcribeBlob(audioBlob: Blob): Promise<{ transcript: string }> {
  const form = new FormData();
  form.append('file', audioBlob, 'audio.webm');
  const res = await fetch(`${BASE}/api/transcribe`, {
    method: 'POST',
    credentials: 'include',
    body: form,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => res.statusText);
    throw new Error(`API ${res.status}: ${text}`);
  }
  return res.json();
}

export const api = {
  voice: {
    transcribe: (audioBlob: Blob) => transcribeBlob(audioBlob),
  },
  me: {
    get: () =>
      request<{
        isOperator: boolean;
        hasImplicitAccess: boolean;
        profile: {
          lifeContext: string;
          workContext: string;
          currentFocus: string;
        };
      }>('/api/me'),
    updateProfile: (data: { lifeContext?: string; workContext?: string; currentFocus?: string }) =>
      request<{ ok: boolean }>('/api/me/profile', {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    tools: () => request<{ toolKeys: ToolKey[] }>('/api/me/tools'),
  },
  coach: {
    me: () =>
      request<
        | { isCoach: false }
        | { isCoach: true; tenant: { id: string; name: string }; userName: string }
      >('/api/coach/me'),
    updateMe: (data: { tenantName?: string; userName?: string }) =>
      request<{ ok: boolean }>('/api/coach/me', {
        method: 'PATCH',
        body: JSON.stringify(data),
      }),
    redeemInvite: (code: string) =>
      request<{ tenantId: string; tenantName: string }>('/api/coach/redeem-invite', {
        method: 'POST',
        body: JSON.stringify({ code }),
      }),
    platformRoleCheck: (email: string) =>
      request<{ hasRole: boolean }>('/api/coach/platform-role-check', {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),
    listClients: (archived?: boolean) => {
      const params = new URLSearchParams();
      if (archived !== undefined) params.set('archived', String(archived));
      const query = params.toString();
      return request<ClientFile[]>(`/api/coach/clients${query ? `?${query}` : ''}`);
    },
    createClient: (data: ClientFileInput) =>
      request<ClientFile>('/api/coach/clients', {
        method: 'POST',
        body: JSON.stringify(data),
      }),
    getClient: (id: string) =>
      request<ClientFile>(`/api/coach/clients/${encodeURIComponent(id)}`),
    updateClient: (id: string, data: Partial<ClientFileInput>) =>
      request<ClientFile>(`/api/coach/clients/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      }),
    archiveClient: (id: string) =>
      request<{ ok: boolean }>(
        `/api/coach/clients/${encodeURIComponent(id)}/archive`,
        { method: 'POST' },
      ),
    restoreClient: (id: string) =>
      request<{ ok: boolean }>(
        `/api/coach/clients/${encodeURIComponent(id)}/restore`,
        { method: 'POST' },
      ),
    deleteClient: (id: string) =>
      request<void>(`/api/coach/clients/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    sessions: {
      list: (clientId: string, opts?: { limit?: number; offset?: number }) => {
        const params = new URLSearchParams();
        if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
        if (opts?.offset !== undefined) params.set('offset', String(opts.offset));
        const query = params.toString();
        return request<{ items: CoachSessionSummary[]; total: number }>(
          `/api/coach/clients/${encodeURIComponent(clientId)}/sessions${query ? `?${query}` : ''}`,
        );
      },
      create: (clientId: string, data: SessionInput) =>
        request<CoachSession>(`/api/coach/clients/${encodeURIComponent(clientId)}/sessions`, {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      get: (id: string) =>
        request<CoachSessionWithDebrief>(`/api/coach/sessions/${encodeURIComponent(id)}`),
      update: (id: string, data: SessionUpdate) =>
        request<CoachSession>(`/api/coach/sessions/${encodeURIComponent(id)}`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }),
      delete: (id: string) =>
        request<void>(`/api/coach/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' }),
      transcribe: (audioBlob: Blob) => transcribeBlob(audioBlob),
      structure: (id: string, transcript: string) =>
        request<CoachSessionDebrief>(`/api/coach/sessions/${encodeURIComponent(id)}/structure`, {
          method: 'POST',
          body: JSON.stringify({ transcript }),
        }),
      refineDebrief: (id: string, fieldName: DebriefFieldName, addition: string) =>
        request<CoachSessionDebrief>(
          `/api/coach/sessions/${encodeURIComponent(id)}/debrief/refine`,
          {
            method: 'POST',
            body: JSON.stringify({ fieldName, addition }),
          },
        ),
      updateDebrief: (id: string, data: DebriefUpdate) =>
        request<CoachSessionDebrief>(`/api/coach/sessions/${encodeURIComponent(id)}/debrief`, {
          method: 'PUT',
          body: JSON.stringify(data),
        }),
      deleteDebrief: (id: string) =>
        request<void>(`/api/coach/sessions/${encodeURIComponent(id)}/debrief`, { method: 'DELETE' }),
      generatePlan: (id: string) =>
        request<PlanGenerationResult>(`/api/coach/sessions/${encodeURIComponent(id)}/generate-plan`, {
          method: 'POST',
        }),
    },
    timeline: (clientId: string) =>
      request<TimelineResponse>(
        `/api/coach/clients/${encodeURIComponent(clientId)}/timeline`,
      ),
    milestones: {
      list: (clientId: string) =>
        request<{ milestones: Milestone[] }>(
          `/api/coach/clients/${encodeURIComponent(clientId)}/milestones`,
        ),
      create: (clientId: string, body: MilestoneInput) =>
        request<{ milestone: Milestone }>(
          `/api/coach/clients/${encodeURIComponent(clientId)}/milestones`,
          { method: 'POST', body: JSON.stringify(body) },
        ),
      update: (milestoneId: string, body: MilestonePatch) =>
        request<{ milestone: Milestone }>(
          `/api/coach/milestones/${encodeURIComponent(milestoneId)}`,
          { method: 'PATCH', body: JSON.stringify(body) },
        ),
      remove: (milestoneId: string) =>
        request<void>(
          `/api/coach/milestones/${encodeURIComponent(milestoneId)}`,
          { method: 'DELETE' },
        ),
    },
    reports: {
      list: (clientId: string) =>
        request<{ reports: ReportSummary[] }>(
          `/api/coach/clients/${encodeURIComponent(clientId)}/reports`,
        ),
      draft: (clientId: string) =>
        request<{ draft: ReportDraft }>(
          `/api/coach/clients/${encodeURIComponent(clientId)}/reports/draft`,
          { method: 'POST', timeoutMs: 60000 },
        ),
      create: (clientId: string, body: ReportSaveInput) =>
        request<{ id: string }>(
          `/api/coach/clients/${encodeURIComponent(clientId)}/reports`,
          { method: 'POST', body: JSON.stringify(body) },
        ),
      get: (reportId: string) =>
        request<{ report: ReportDetail }>(
          `/api/coach/reports/${encodeURIComponent(reportId)}`,
        ),
      update: (reportId: string, body: ReportPatch) =>
        request<{ report: ReportDetail }>(
          `/api/coach/reports/${encodeURIComponent(reportId)}`,
          { method: 'PATCH', body: JSON.stringify(body) },
        ),
      remove: (reportId: string) =>
        request<void>(`/api/coach/reports/${encodeURIComponent(reportId)}`, {
          method: 'DELETE',
        }),
      pdfUrl: (reportId: string) =>
        `${BASE}/api/coach/reports/${encodeURIComponent(reportId)}/pdf`,
    },
    listClientTools: (clientId: string) =>
      request<ClientToolRow[]>(`/api/coach/clients/${clientId}/tools`),
    grantClientTool: (clientId: string, toolKey: ToolKey) =>
      request<{ granted: true; mailed: boolean }>(
        `/api/coach/clients/${clientId}/tools/${toolKey}`,
        { method: 'POST' },
      ),
    revokeClientTool: (clientId: string, toolKey: ToolKey) =>
      request<{ revoked: true }>(
        `/api/coach/clients/${clientId}/tools/${toolKey}`,
        { method: 'DELETE' },
      ),
  },
  announcements: {
    list: () =>
      request<Array<{
        id: string;
        title: string;
        body: string;
        linkHref: string | null;
        linkLabel: string | null;
        publishedAt: string;
      }>>('/api/announcements'),
    unseen: () =>
      request<Array<{
        id: string;
        title: string;
        body: string;
        linkHref: string | null;
        linkLabel: string | null;
        publishedAt: string;
      }>>('/api/announcements/unseen'),
    markSeen: () =>
      request<{ ok: boolean }>('/api/announcements/mark-seen', { method: 'POST' }),
  },
  team: {
    list: () => request<TeamMember[]>('/api/team'),
    create: (m: Omit<TeamMember, 'createdAt' | 'updatedAt'>) =>
      request<TeamMember>('/api/team', { method: 'POST', body: JSON.stringify(m) }),
    update: (id: string, m: Omit<TeamMember, 'id' | 'createdAt' | 'updatedAt'>) =>
      request<TeamMember>(`/api/team/${id}`, { method: 'PUT', body: JSON.stringify(m) }),
    delete: (id: string) =>
      request<void>(`/api/team/${id}`, { method: 'DELETE' }),
  },
  sessions: {
    list: () => request<ChatSession[]>('/api/sessions'),
    create: (data: { type: SessionType; title?: string; id?: string; memberIds?: string[] }) =>
      request<ChatSession>('/api/sessions', { method: 'POST', body: JSON.stringify(data) }),
    get: (id: string) => request<ChatSession>(`/api/sessions/${id}`),
    patch: (id: string, data: { title?: string; addMemberId?: string; removeMemberId?: string }) =>
      request<ChatSession>(`/api/sessions/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    appendMessage: (sessionId: string, msg: Pick<ChatMessage, 'role' | 'content' | 'timestamp'>) =>
      request<ChatMessage>(`/api/sessions/${sessionId}/messages`, {
        method: 'POST',
        body: JSON.stringify(msg),
      }),
    delete: (id: string) => request<void>(`/api/sessions/${id}`, { method: 'DELETE' }),
  },
  sync: {
    push: (payload: { members: TeamMember[]; sessions?: ChatSession[] }) =>
      request<{ synced: { members: number; sessions: number } }>('/api/sync', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
  },
  avatar: {
    generate: (memberId: string | undefined, appearanceDescription: string) =>
      request<{ avatarImageUrl: string }>('/api/avatar/generate', {
        method: 'POST',
        body: JSON.stringify({ memberId, appearanceDescription }),
      }),
    regenerate: (memberId: string, sessionId?: string) =>
      request<{ avatarImageUrl: string; appearanceDescription: string }>('/api/avatar/regenerate', {
        method: 'POST',
        body: JSON.stringify({ memberId, sessionId }),
      }),
  },
  ai: {
    stream: async function* (
      messages: Array<{ role: 'user' | 'assistant'; content: string }>,
      opts: { intent: 'discovery' | 'team-consult' | 'member-consult'; params?: { memberId?: string } },
    ): AsyncGenerator<{ type: 'text_delta'; text: string } | { type: 'done' } | { type: 'error'; message: string }> {
      const res = await fetch(`${BASE}/api/ai/stream`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          intent: opts.intent,
          params: opts.params,
          messages,
        }),
      });
      if (!res.ok || !res.body) {
        const text = await res.text().catch(() => res.statusText);
        if (res.status === 422) {
          try {
            const body = JSON.parse(text);
            if (body?.error === 'voucher_required') {
              throw new VoucherRequiredError(body.message ?? 'Voucher required');
            }
          } catch (err) {
            if (err instanceof VoucherRequiredError) throw err;
          }
        }
        throw new Error(`API ${res.status}: ${text}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            yield JSON.parse(line.slice(6));
          }
        }
      }
    },
    deriveAppearance: (name: string, role: string, description: string) =>
      request<{ description: string }>('/api/ai/derive-appearance', {
        method: 'POST',
        body: JSON.stringify({ name, role, description }),
      }),
    suggestMusic: (memberId: string) =>
      request<{
        tracks: Array<{
          id: string;
          name: string;
          artists: string[];
          albumImageUrl: string | null;
          previewUrl: string | null;
          externalUrl: string;
          reason: string;
        }>;
      }>('/api/ai/suggest-music', {
        method: 'POST',
        body: JSON.stringify({ memberId }),
      }),
  },
  vouchers: {
    redeem: (code: string) =>
      request<{ code: string; usedCount: number; usageLimit: number; scope: 'per_user' | 'global' }>('/api/vouchers/redeem', {
        method: 'POST',
        body: JSON.stringify({ code }),
      }),
    status: () =>
      request<
        | { active: false }
        | {
            active: true;
            code: string;
            usedCount: number;
            usageLimit: number;
            scope: 'per_user' | 'global';
            totalUsed: number;
            expiresAt: string | null;
            revokedAt: string | null;
          }
      >('/api/vouchers/status'),
  },
  operator: {
    stats: () =>
      request<{ userCount: number; redemptionCount: number; voucherCount: number; totalUsage: number }>(
        '/api/operator/stats',
      ),
    vouchers: {
      list: () =>
        request<Array<{
          code: string;
          usageLimit: number;
          scope: 'per_user' | 'global';
          maxRedemptions: number | null;
          isStarter: boolean;
          totalUsed: number;
          expiresAt: string | null;
          campaignTag: string | null;
          createdAt: string;
          revokedAt: string | null;
          redemptionCount: number;
        }>>('/api/operator/vouchers'),
      create: (input: {
        code?: string;
        usageLimit: number;
        scope: 'per_user' | 'global';
        maxRedemptions?: number | null;
        isStarter?: boolean;
        expiresAt?: string | null;
        campaignTag?: string | null;
      }) =>
        request<{
          code: string;
          usageLimit: number;
          scope: 'per_user' | 'global';
          maxRedemptions: number | null;
          isStarter: boolean;
          totalUsed: number;
          expiresAt: string | null;
          campaignTag: string | null;
          createdAt: string;
          revokedAt: string | null;
        }>('/api/operator/vouchers', {
          method: 'POST',
          body: JSON.stringify(input),
        }),
      revoke: (code: string) =>
        request<{ ok: boolean }>(`/api/operator/vouchers/${encodeURIComponent(code)}/revoke`, {
          method: 'POST',
        }),
      get: (code: string) =>
        request<{
          code: string;
          usageLimit: number;
          scope: 'per_user' | 'global';
          maxRedemptions: number | null;
          isStarter: boolean;
          totalUsed: number;
          expiresAt: string | null;
          campaignTag: string | null;
          createdAt: string;
          revokedAt: string | null;
          redemptions: Array<{
            userId: string;
            userName: string;
            userEmail: string;
            usedCount: number;
            redeemedAt: string;
            replacedAt: string | null;
          }>;
        }>(`/api/operator/vouchers/${encodeURIComponent(code)}`),
    },
    users: {
      list: () =>
        request<Array<{
          id: string;
          name: string;
          email: string;
          createdAt: string;
          isOperator: boolean;
          voucher: {
            code: string;
            usedCount: number;
            usageLimit: number;
            scope: 'per_user' | 'global';
            revoked: boolean;
          } | null;
          historyCount: number;
          coach: {
            tenantId: string;
            tenantName: string;
            clientCount: number;
          } | null;
        }>>('/api/operator/users'),
      get: (id: string) =>
        request<{
          id: string;
          name: string;
          email: string;
          createdAt: string;
          isOperator: boolean;
          redemptions: Array<{
            id: string;
            voucherCode: string;
            voucherScope: 'per_user' | 'global';
            voucherUsageLimit: number;
            voucherRevokedAt: string | null;
            usedCount: number;
            redeemedAt: string;
            replacedAt: string | null;
          }>;
        }>(`/api/operator/users/${encodeURIComponent(id)}`),
    },
    announcements: {
      list: () =>
        request<Array<{
          id: string;
          title: string;
          body: string;
          linkHref: string | null;
          linkLabel: string | null;
          publishedAt: string;
        }>>('/api/operator/announcements'),
      create: (data: { title: string; body: string; linkHref?: string; linkLabel?: string }) =>
        request<{
          id: string;
          title: string;
          body: string;
          linkHref: string | null;
          linkLabel: string | null;
          publishedAt: string;
        }>('/api/operator/announcements', {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      delete: (id: string) =>
        request<void>(`/api/operator/announcements/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    },
    coachInvites: {
      list: () => request<CoachInvite[]>('/api/operator/coach-invites'),
      create: (data: { code?: string; expiresAt?: string | null; note?: string | null }) =>
        request<CoachInvite>('/api/operator/coach-invites', {
          method: 'POST',
          body: JSON.stringify(data),
        }),
      revoke: (code: string) =>
        request<{ ok: boolean }>(
          `/api/operator/coach-invites/${encodeURIComponent(code)}/revoke`,
          { method: 'POST' },
        ),
    },
    analytics: (range?: { from?: string; to?: string }) => {
      const params = new URLSearchParams();
      if (range?.from) params.set('from', range.from);
      if (range?.to) params.set('to', range.to);
      const query = params.toString();
      return request<{
        range: { from: string; to: string };
        summary: { uniqueUsers: number; totalMessages: number };
        daily: Array<{ date: string; activeUsers: number; messages: number }>;
        topUsers: Array<{
          userId: string;
          name: string;
          email: string;
          voucherCode: string;
          usedCount: number;
          usageLimit: number;
          scope: 'per_user' | 'global';
        }>;
        voucherUtilization: Array<{
          code: string;
          scope: 'per_user' | 'global';
          usageLimit: number;
          totalUsed: number;
          redemptionCount: number;
          utilizationPercent: number;
        }>;
      }>(`/api/operator/analytics${query ? `?${query}` : ''}`);
    },
  },
};
